import { useState, useEffect } from 'react';

export interface LocationData {
  country_code: string;
  country_name: string;
  currency: string;
}

export interface LocationState {
  countryCode: string | null;
  countryName: string | null;
  isUS: boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook for detecting user's location based on IP address
 * Independent of currency selection logic
 * No caching - always fetches fresh location data
 */
export const useLocation = (): LocationState => {
  const [locationState, setLocationState] = useState<LocationState>({
    countryCode: null,
    countryName: null,
    isUS: false,
    isLoading: true,
    error: null
  });

  useEffect(() => {
    const detectLocation = async () => {
      try {
        // Always fetch fresh location data (no caching)
        const response = await fetch('https://ipapi.co/json/', {
          method: 'GET',
          headers: {
            'User-Agent': 'JamPackd-Location-Detection/1.0'
          }
        });

        if (!response.ok) {
          throw new Error(`Location service failed: ${response.status}`);
        }

        const data: LocationData = await response.json();

        setLocationState({
          countryCode: data.country_code,
          countryName: data.country_name || null,
          isUS: data.country_code.toUpperCase() === 'US',
          isLoading: false,
          error: null
        });

      } catch (error) {
        setLocationState({
          countryCode: null,
          countryName: null,
          isUS: false, // Default to false if detection fails
          isLoading: false,
          error: error instanceof Error ? error.message : 'Location detection failed'
        });
      }
    };

    detectLocation();
  }, []);

  return locationState;
};

export default useLocation;